import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { Banner, Button, Icon, Modal, Note, Progress, Skeleton, Tag } from '@repo/design-system';
import {
  focus,
  fontMono,
  ink,
  inkOn,
  line,
  motion,
  radius,
  rgba,
  semantic,
  shadow,
  space,
  surface,
  typeRole,
  weight,
} from '@repo/design-tokens';
import {
  PointMarker,
  plannerTheme,
  ReadOnlyCanvas,
  usePlanMachineFootprints,
  type CanvasMarker,
  type CanvasPointMarker,
} from '@repo/ui/planner';
import type { PointMarkerState } from '@repo/ui/planner';
import type { CameraWallFeed } from '@repo/ui';
import {
  calibrationProblem,
  calibrationTargetsMoved,
  findLatestDesign,
  isValidCalibration,
  pictureUpHeading,
  placementOf,
  planScrollBounds,
} from '@repo/front-end-utils';
import type { FloorPlannerCamera, FloorPlannerLayout } from '@repo/frontend-types';

import {
  buildCalibration,
  canSave,
  DISAGREE_NOTE,
  draftReducer,
  edgeLetter,
  fieldOfView,
  fitViews,
  guidedCorners,
  guidedSetup,
  guidedTarget,
  initialDraft,
  viewFitCacheFrom,
  markerOf,
  markerSuggestion,
  MIN_EDGE_POINTS,
  overlayFor,
  pairNumber,
  pictureRegion,
  plural,
  readoutFor,
  refusalSentence,
  takenKeys,
  targetPlanOf,
  tileLetter,
  tileSummary,
  type CalibrationDraft,
  type DraftPair,
  type GuidedCorner,
  type GuidedTarget,
  type Point,
  type ReadoutTone,
  type ViewFit,
} from './calibrationModel';
import {
  CAMERA_TARGET_PAD_M,
  cameraCoverageBoxPx,
  cameraViewRotation,
  cameraMarkM,
  cameraWedgeM,
  matchPictureRotation,
  planFrameFor,
  towardCameraArrowM,
} from './calibrationPlanView';
import { CALIBRATION_LOOK } from './calibrationLook';
import { standInText } from './calibrationStatusText';
import { naturalToPane, useImageZoomPan } from './useImageZoomPan';
import { useModelFootprints } from './useModelFootprints';

export interface CameraCalibrationProps {
  /** The plan camera being calibrated. Its stream must have a picture (R1). */
  camera: FloorPlannerCamera;
  /** What the step calls it ("Cardio", "Camera 3"). */
  cameraName: string;
  layout: FloorPlannerLayout;
  /** The camera's floor: the active floor, because `updateCamera` writes there. */
  floorIndex: number;
  floorName: string;
  /** The wall's feed for the camera's stream: its latest picture. */
  feed: CameraWallFeed;
  /**
   * The step's camera write (`writeCamera`). Save sends `{ calibration }` with `report` true, so the
   * draft saver saves it like a link change.
   */
  onWrite: (refid: string, patch: Partial<FloorPlannerCamera>, report: boolean) => void;
  /** Close the screen: `saved` after a save, `cancelled` otherwise. */
  onClose: (outcome: 'saved' | 'cancelled') => void;
  /** Re-read the stream's picture (the wall's per-feed refresh), for Retry after a failed load. */
  onRetryPicture?: (id: CameraWallFeed['id']) => void;
}

/** The picture overlay's lines, from the one calibration look table. */
const { room, machine, spotError } = CALIBRATION_LOOK.overlay;

/** A press that moves less than this (screen px) is a click, not a pan or a drag. */
const CLICK_TOLERANCE = 4;
/** The picture crosshair's arrow step, in screen px (Shift: ten times it). */
const PICTURE_STEP_PX = 2;

/** The Inputs table's sentence when "Can't see it" has nothing left to offer. */
export const NO_MORE_CORNERS = 'No more corners to offer: save with what you have, or start again';

/** How near a plan click must land to an open corner to choose it, in metres (findable-spots R4.1). */
export const PICK_RADIUS_M = 0.75;

/** No corners passed: one array, so an unsteered screen keeps one identity. */
const NO_KEYS: string[] = [];

/** The plan pane's accessible name (findable-spots, criterion 7). */
export const PLAN_PANE_NAME =
  'Floor plan: click a corner to choose it, or find the highlighted one';

/** The sentence when a plan click reaches no open corner (findable-spots R4.2). */
export const NO_CORNER_THERE =
  'No corner to find there. Click near a corner of an outlined machine.';

/**
 * The open corner nearest a plan click, within `PICK_RADIUS_M` (findable-spots R4.1): one offered
 * by `guidedCorners` and neither placed nor skipped. Null when none is that near.
 */
export const cornerNear = (
  point: { x: number; y: number },
  corners: ReadonlyArray<GuidedCorner>,
  taken: ReadonlySet<string>,
): GuidedCorner | null => {
  let best: GuidedCorner | null = null;
  let bestD = PICK_RADIUS_M;
  for (const corner of corners) {
    if (taken.has(corner.key)) continue;
    const d = Math.hypot(corner.plan.x - point.x, corner.plan.y - point.y);
    if (d <= bestD) {
      best = corner;
      bestD = d;
    }
  }
  return best;
};

/**
 * Where to look, for the instruction (findable-spots R3): the part of the tile the estimate puts the
 * corner in, _should be_ on a fitted tile and _probably_ on a first guess. Empty when the estimate
 * cannot project it.
 */
export const whereToLook = (
  target: Pick<GuidedTarget, 'expected' | 'fitted' | 'view'>,
  rect: ViewFit['rect'] | undefined,
  tiled: boolean,
): string => {
  if (!target.expected || !rect) return '';
  const part = pictureRegion(target.expected, rect);
  const of = tiled ? `of tile ${tileLetter(target.view)}` : 'of the picture';
  return target.fitted
    ? `It should be near the ${part} ${of}.`
    : `It is probably near the ${part} ${of}.`;
};

/** "28 Sept 22:35": when the picture was taken. */
const takenAt = (d: Date | null | undefined) =>
  d
    ? d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

/**
 * The plan's machine outlines (`observer-matching-beyond-geometry-calibration-plan-outlines`, R1 and
 * R2): one per machine whose size was read, through the four corners the guided target is chosen
 * from, so the highlighted corner sits on a drawn corner. The target's machine is drawn last, in the
 * Observers stage's picked look; the rest in a thin neutral line with no fill.
 */
const planOutlineMarkers = (
  corners: ReadonlyArray<GuidedCorner>,
  targetItem: string | null,
): CanvasMarker[] => {
  const byItem = new Map<string, Array<readonly [number, number]>>();
  for (const corner of corners) {
    const polygon = byItem.get(corner.itemRefid) ?? [];
    polygon[corner.corner] = [corner.plan.x, corner.plan.y];
    byItem.set(corner.itemRefid, polygon);
  }
  const rest: CanvasMarker[] = [];
  let picked: CanvasMarker | null = null;
  for (const [id, polygonM] of Array.from(byItem)) {
    if (id === targetItem) {
      picked = {
        id,
        color: CALIBRATION_LOOK.overlay.targetMachine.stroke,
        fill: CALIBRATION_LOOK.overlay.targetMachine.fill,
        shape: 'outline',
        emphasised: true,
        polygonM,
      };
    } else {
      rest.push({ id, color: line.selected, shape: 'outline', polygonM });
    }
  }
  return picked ? [...rest, picked] : rest;
};

/** "Six", for the announcement at six spots; the number itself beyond ten. */
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
const countWord = (n: number) => NUMBER_WORDS[n] ?? String(n);

/**
 * The calibration screen (`observer-matching-beyond-geometry-guided-calibration`; first built as
 * `observer-autogen-plan-projection`, design card "Camera Calibration"): the camera's picture on the
 * left and its floor's plan on the right. The plan highlights one machine corner at a time, chosen
 * for the most information (R3), and the admin clicks the same corner on the picture, or presses
 * "Can't see it" for another. Six spots per tile save (R4); the lens and the layout are chosen for
 * the admin (R5). Every tile of the picture is fitted on its own as a physical camera; machine and
 * room outlines are drawn over the picture through the fit, and the readout says how good it is in
 * px and metres. Save writes a version-2 calibration, each spot with the corner it was asked for,
 * through the step's `writeCamera`, which asks the draft saver for a save.
 */
const CameraCalibration: React.FC<CameraCalibrationProps> = ({
  camera,
  cameraName,
  layout,
  floorIndex,
  floorName,
  feed,
  onWrite,
  onClose,
  onRetryPicture,
}) => {
  const titleId = useId();
  const floor = layout.floors[floorIndex];
  const design = floor ? findLatestDesign(floor.designs) : null;
  const items = useMemo(() => design?.items ?? [], [design]);
  const surfaces = useMemo(() => design?.surfaces ?? [], [design]);
  const walls = useMemo(() => design?.walls ?? [], [design]);
  // Each machine's footprint from its model file's header (footprint-calibration R3, R6).
  const modelFiles = useMemo(
    () => items.filter(item => !item._deleted).map(item => item.modelFile),
    [items],
  );
  const machineFootprints = useModelFootprints(modelFiles);
  /** What has been read so far: a later read adds candidates (Failure States, footprints). */
  const readFootprints = machineFootprints.arrived ?? machineFootprints.footprints;
  // The plan pane's fit measures machines by the same footprints every plan does (konva-removal R5).
  const footprintOf = usePlanMachineFootprints();
  const placement = placementOf(floor);

  /**
   * The stored calibration to reopen, fitted on this camera's stream: a version-2 one. Anything
   * else, a version-1 value included, opens a fresh guided run, with a warning.
   */
  const storedValue = camera.calibration as unknown;
  const storedStream = (storedValue as { rtspEndpointBid?: unknown } | undefined)?.rtspEndpointBid;
  const stored: unknown =
    storedValue != null && storedStream === camera.rtspEndpointBid ? storedValue : null;
  /** The size the stored points were placed on, read with a guard (the value may be any shape). */
  const storedFrame = (() => {
    if (typeof stored !== 'object' || stored === null) return null;
    const { frameWidth, frameHeight } = stored as Record<string, unknown>;
    return typeof frameWidth === 'number' && typeof frameHeight === 'number'
      ? { width: frameWidth, height: frameHeight }
      : null;
  })();
  const storedCurrent = isValidCalibration(stored) ? stored : null;
  const [draft, dispatch] = useReducer(draftReducer, undefined, () => initialDraft(stored));
  const warnedUnreadable = useRef(false);
  useEffect(() => {
    if (warnedUnreadable.current || storedValue == null) return;
    const problem = calibrationProblem(storedValue);
    if (problem && draft.pairs.length === 0) {
      warnedUnreadable.current = true;
      console.warn('[calibration] the saved calibration could not be read', {
        camera: camera.refid,
        field: problem,
      });
    }
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  /*
   * The lens and the layout (R5): a reopened calibration is shown as it was saved; a guided run uses
   * the camera's own — Fisheye at 180° or more, or after a Fisheye save; four tiles at 360°.
   */
  const setup = useMemo(
    () =>
      draft.mode === 'saved' && storedCurrent
        ? { layout: storedCurrent.layout, lens: storedCurrent.lensModel }
        : guidedSetup(camera),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft.mode, storedCurrent?.layout, storedCurrent?.lensModel, camera.fovAngle],
  );

  /* ---------------- the picture ---------------- */

  /*
   * The last picture that loaded, with the URL it loaded from. Its size is the picture's natural
   * size only while that URL is still the feed's: a refreshed picture (a new URL) has no size until
   * it loads, so nothing is placed or saved against a picture the admin cannot see yet. The last
   * size is kept for the zoom, so a refresh does not reset the view, and the same object is reused
   * when the new picture has the same size, so the size-change check below sees no change.
   */
  const [loaded, setLoaded] = useState<{
    url: string;
    size: { width: number; height: number };
  } | null>(null);
  const [pictureFailed, setPictureFailed] = useState(false);
  const imageUrl = feed.imageUrl;
  const natural = loaded && imageUrl && loaded.url === imageUrl ? loaded.size : null;
  useEffect(() => {
    setPictureFailed(false);
  }, [imageUrl]);
  const onPictureLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const url = imageUrl;
    if (url && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setLoaded(prev => {
        const sameSize =
          prev && prev.size.width === img.naturalWidth && prev.size.height === img.naturalHeight;
        if (sameSize && prev.url === url) return prev;
        return {
          url,
          size: sameSize ? prev.size : { width: img.naturalWidth, height: img.naturalHeight },
        };
      });
    }
    setPictureFailed(false);
  };
  const onPictureError = () => {
    setPictureFailed(true);
    // Never shown as "no picture": the stream has one, and the read of it failed.
    console.error('[calibration] the camera picture could not be loaded', {
      stream: camera.rtspEndpointBid,
      url: imageUrl,
    });
  };
  /** No picture to show: the stream gave no URL, or the read of it failed. Never "no picture". */
  const pictureUnavailable = !imageUrl || pictureFailed;
  const pictureReady = !!imageUrl && natural !== null && !pictureFailed;

  /** The size the picture is laid out at: the last one loaded, kept while a refresh loads. */
  const shownSize = loaded?.size ?? null;
  const zoom = useImageZoomPan(shownSize);

  /*
   * Spots are in one picture's pixels, so they never survive a change of size. A stored calibration
   * reopened on a picture of another size is out of date (R11), and a picture that changes size while
   * the screen is open (the stream was reconfigured) would mix spots from two sizes in one Save. In
   * both cases the spots are cleared and a guided run starts, and the screen says why.
   */
  const [sizeNote, setSizeNote] = useState<string | null>(null);
  const lastSize = useRef<{ width: number; height: number } | null>(storedFrame);
  useEffect(() => {
    if (!natural) return;
    const before = lastSize.current;
    lastSize.current = natural;
    if (!before || (before.width === natural.width && before.height === natural.height)) return;
    const placed = draftRef.current;
    if (placed.pairs.length === 0 && placed.edges.length === 0) return;
    dispatch({ type: 'start-again' });
    setSizeNote(
      `The camera picture is ${natural.width} × ${natural.height}, but the spots were placed on a ${before.width} × ${before.height} picture, so they were cleared. Place them again.`,
    );
  }, [natural]);
  // Once the admin places spots again, the note has done its job.
  useEffect(() => {
    if (draft.pairs.length > 0) setSizeNote(null);
  }, [draft.pairs.length]);

  /* ---------------- the fit, the target, and what is drawn from them ---------------- */

  /**
   * Each tile's last fit: a refit starts from it, and a drag in one tile refits that tile alone. A
   * reopened calibration seeds it with the stored cameras, so the fit shown is the stored one (R9).
   */
  const [seededFits] = useState(() => viewFitCacheFrom(stored));
  const fitCache = useRef(seededFits);
  /** The camera's plan marker: the fit starts from it and is judged against it (R3, R6). */
  const marker = useMemo(
    () => markerOf(camera),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera.refid, camera.x, camera.y, camera.rotation, camera.fovAngle],
  );
  /*
   * The size the fit uses: the picture's natural size, or — while a refreshed picture of the same
   * stream loads (Retry, a new snapshot) — the last size that loaded, so the fit, the target and
   * Move marker do not drop out and back. A picture of another size clears the spots (above), and
   * placing and Save stay gated on `pictureReady`.
   */
  const fitSize = natural ?? shownSize;
  const fits = useMemo<ViewFit[]>(
    () =>
      fitSize
        ? fitViews(
            { pairs: draft.pairs, edges: draft.edges, ...setup },
            fitSize,
            marker,
            fitCache.current,
          )
        : [],
    // The fit depends on the spots, the edges, the setup, the marker and the picture size — never
    // on the selection, the zoom or the pan.
    [draft.pairs, draft.edges, setup, fitSize, marker],
  );
  /** The first fitted tile, row-major: the marker suggestion reads it. */
  const firstFit = fits.find(f => f.verdict === 'ok' && f.model) ?? null;

  /** Every corner the screen may ask for (R3): machines whose size was read, those in sight first. */
  const corners = useMemo(
    () => guidedCorners(items, readFootprints, { camera, walls }),
    [items, readFootprints, camera, walls],
  );
  /*
   * The person's own steer (findable-spots R4): a corner picked on the plan, and the corners
   * Another corner moved past. Both belong to the spots and skips they were made against, so a
   * place, a skip, a removal or Start again clears them without an effect.
   */
  const [steer, setSteer] = useState<{
    chosen: string | null;
    passed: string[];
    pairs: CalibrationDraft['pairs'];
    skipped: CalibrationDraft['skipped'];
  } | null>(null);
  const steering =
    steer && steer.pairs === draft.pairs && steer.skipped === draft.skipped ? steer : null;
  const chosen = steering?.chosen ?? null;
  const passed = steering?.passed ?? NO_KEYS;
  /*
   * The corner last offered, with the spots, skips, fits and steer it was offered against. A later
   * footprint read adds corners but must not move the highlight under the admin's click (Failure
   * States: "the current target stays"), so while those are unchanged it is held. A place, skip,
   * remove, move, start again, refit, pick or Another corner gives new ones, and the choice is made
   * afresh.
   */
  const offered = useRef<{
    key: string;
    view: number;
    pairs: CalibrationDraft['pairs'];
    skipped: CalibrationDraft['skipped'];
    fits: ViewFit[];
    chosen: string | null;
    passed: string[];
  } | null>(null);
  const guided = useMemo(
    () => {
      const last = offered.current;
      const held =
        last &&
        last.pairs === draft.pairs &&
        last.skipped === draft.skipped &&
        last.fits === fits &&
        last.chosen === chosen &&
        last.passed === passed
          ? { key: last.key, view: last.view }
          : null;
      const next = guidedTarget({
        draft,
        fits,
        corners,
        settled: machineFootprints.settled,
        marker,
        ...setup,
        held,
        chosen,
        passed,
      });
      offered.current = next.target
        ? {
            key: next.target.key,
            view: next.target.view,
            pairs: draft.pairs,
            skipped: draft.skipped,
            fits,
            chosen,
            passed,
          }
        : null;
      return next;
    },
    // The target depends on the spots, the skipped corners, the fits, the corners on offer and the
    // person's steer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      draft.pairs,
      draft.skipped,
      draft.spotsAsked,
      draft.mode,
      fits,
      corners,
      machineFootprints.settled,
      marker,
      setup,
      chosen,
      passed,
    ],
  );
  const target = guided.target;
  const targetItem = target?.itemRefid ?? null;
  /** The camera on the plan (findable-spots R1.2), out to the target, or to its reach with none. */
  const cameraWedge = useMemo(() => {
    const reach = target
      ? Math.hypot(target.plan.x - camera.x, target.plan.y - camera.y) + CAMERA_TARGET_PAD_M
      : (camera.detectionDistance ?? 0);
    // The fit's own field of view, so the plan shows the coverage the estimate uses.
    return cameraWedgeM({ ...camera, fovAngle: fieldOfView(camera) }, reach);
  }, [camera, target]);
  /*
   * The frame while a corner is asked for (target-framing R1): the camera with the target when it is
   * near, else the target alone, with an arrow to the camera (R2).
   */
  const planFrame = useMemo(
    () => (target ? planFrameFor(camera, target.plan) : null),
    [camera, target],
  );
  const towardCamera = useMemo(
    () =>
      target && planFrame && !planFrame.withCamera ? towardCameraArrowM(camera, target.plan) : null,
    [camera, target, planFrame],
  );
  /** The machines' outlines on the plan, the target's emphasised (plan outlines R1, R2). */
  const planOutlines = useMemo(
    () => [
      ...(cameraWedge
        ? [
            {
              id: `camera:${camera.refid}`,
              color: CALIBRATION_LOOK.overlay.camera.stroke,
              fill: CALIBRATION_LOOK.overlay.camera.fill,
              shape: 'outline' as const,
              polygonM: cameraWedge,
            },
            // Its position too: a 360° camera's circle has no apex to show where it hangs.
            {
              id: `camera:${camera.refid}:at`,
              color: CALIBRATION_LOOK.overlay.camera.stroke,
              shape: 'outline' as const,
              polygonM: cameraMarkM(camera),
            },
          ]
        : []),
      ...(towardCamera
        ? [
            {
              id: `camera:${camera.refid}:toward`,
              color: CALIBRATION_LOOK.overlay.camera.stroke,
              fill: CALIBRATION_LOOK.overlay.camera.stroke,
              shape: 'outline' as const,
              polygonM: towardCamera,
            },
          ]
        : []),
      ...planOutlineMarkers(corners, targetItem),
    ],
    [corners, targetItem, cameraWedge, towardCamera, camera],
  );
  const placedCount = draft.pairs.length;

  /** R10 on this screen: a reopened guided calibration whose machine has moved since. */
  const targetsMoved = useMemo(
    () =>
      draft.mode === 'saved' && storedCurrent
        ? calibrationTargetsMoved(storedCurrent, targetPlanOf(items, readFootprints))
        : false,
    [draft.mode, storedCurrent, items, readFootprints],
  );

  /*
   * The plan pane (R6): turned to the camera's view — up the picture is up the pane through the
   * tile's fit (Match picture, always on), or the marker's heading before a fit — and fitted to
   * the camera and the target together when the camera is within 10 m, else the target alone with
   * an arrow to the camera (target-framing R1, R2), or to the camera's coverage with no
   * target.
   */
  const viewFit =
    (target ? fits[target.view] : null)?.verdict === 'ok' ? fits[target!.view] : firstFit;
  const planRotation = viewFit?.model
    ? matchPictureRotation(pictureUpHeading(viewFit.model))
    : cameraViewRotation(camera, placement);
  const coverageBounds = useMemo(
    () =>
      design
        ? cameraCoverageBoxPx(
            camera,
            planScrollBounds(design, { underlays: 'none', includeCameras: false, footprintOf }),
          )
        : null,
    [design, camera, footprintOf],
  );
  // The frame `planFrameFor` chose (target-framing R1), or the coverage with no target.
  const planFitBounds = planFrame ? planFrame.box : coverageBounds;

  const suggestionToMove = firstFit
    ? markerSuggestion(firstFit, { fovAngle: marker.fovAngle })
    : null;
  /**
   * Memoised on the fit, the plan and the machine footprints (which settle once): zoom and pan are
   * a transform and never redraw it (R8).
   */
  const overlay = useMemo(
    () => overlayFor(fits, items, surfaces, machineFootprints),
    [fits, items, surfaces, machineFootprints],
  );
  // Only once machine outlines are drawn — a fitted tile — so the line never describes outlines
  // that are not on screen. `overlay.standIns` is zero until the footprints settle.
  const standIns = fits.some(fit => fit.verdict === 'ok' && fit.model)
    ? standInText(overlay.standIns)
    : null;
  const readout = useMemo(
    () => (fits.length ? readoutFor(fits, draft, setup.lens) : null),
    [fits, draft, setup.lens],
  );
  /*
   * A reopened calibration is saved only once something changed (R9): a move, a removal, an edge or
   * Recalibrate. Unchanged, Save would only rewrite the stored fit.
   */
  const saveable = pictureReady && canSave(fits) && (draft.mode !== 'saved' || draft.dirty);
  const badIds = useMemo(() => new Set(fits.flatMap(f => f.badPairIds)), [fits]);
  /** Each fitted spot's error in picture px and floor metres (R5). */
  const errorOf = useMemo(() => {
    const map = new Map<string, { px: number | null; m: number | null }>();
    for (const f of fits) f.pairs.forEach((p, k) => f.errors && map.set(p.id, f.errors.pairs[k]));
    return map;
  }, [fits]);
  /** The spot that disagrees with the others, per tile (R5, R9). */
  const disagreeingIds = useMemo(
    () => new Set(fits.map(f => f.disagreeingPairId).filter((id): id is string => id !== null)),
    [fits],
  );
  const refusal = fits.map(f => refusalSentence(f.verdict)).find(Boolean) ?? null;

  /*
   * Announcements (Block A 4). The screen is a modal dialog, so its live regions are its own: the
   * step's region sits outside the dialog. Polite: each new target, each spot placed, a refusal and
   * six spots placed. The picture failing is assertive.
   */
  const [polite, setPolite] = useState('');
  const politeNonce = useRef(0);
  const say = useCallback((text: string) => {
    politeNonce.current += 1;
    // A trailing no-break space on every other message, so a repeat still changes the region.
    setPolite(`${text}${politeNonce.current % 2 === 0 ? ' ' : ''}`);
  }, []);
  /** The spot just placed, announced with what comes next. */
  const justPlaced = useRef<number | null>(null);
  const spotOf = Math.min(placedCount + 1, guided.wanted);
  // Where to look (findable-spots R3), and whose corner a plan pick chose (R4.5).
  const lookText = target
    ? whereToLook(target, fits[target.view]?.rect, setup.layout === 'grid-2x2')
    : '';
  const targetText = target
    ? [
        target.chosen ? `You chose the corner of ${target.name}` : null,
        `Spot ${spotOf} of ${guided.wanted}: find the highlighted corner of ${target.name} on the picture`,
        lookText ? lookText.replace(/\.$/, '') : null,
      ]
        .filter(Boolean)
        .join('. ')
    : null;
  const doneText =
    guided.status === 'done' && saveable
      ? `${countWord(placedCount)} spots placed: you can save`
      : null;
  const announceKey = [
    target?.key ?? '',
    // A pick of the corner already asked for still says so (criterion 7).
    target?.chosen ? 'chosen' : '',
    placedCount,
    refusal?.announce ?? '',
    doneText ?? '',
    guided.status,
  ].join('|');
  useEffect(() => {
    const parts: string[] = [];
    if (justPlaced.current !== null) parts.push(`Spot ${justPlaced.current} placed`);
    justPlaced.current = null;
    // A refused fit keeps asking (Failure States), so its sentence comes before the next target.
    if (refusal) parts.push(refusal.announce);
    if (doneText) parts.push(doneText);
    else if (targetText) parts.push(targetText);
    else if (guided.status === 'exhausted') parts.push(NO_MORE_CORNERS);
    if (parts.length) say(parts.join('. '));
    // Announced when what is asked for, or what is placed, changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announceKey]);

  /** Move marker (R10): the fitted position, absolute, through the step's camera write, once. */
  const moveMarker = () => {
    if (!suggestionToMove) return;
    onWrite(camera.refid, suggestionToMove.patch, true);
    say(`Camera marker moved ${suggestionToMove.distanceM.toFixed(1)} m`);
  };
  /*
   * Move marker comes and goes with the fit. When it goes while it has focus, focus would fall to
   * the page outside the dialog; it moves to the readout instead.
   */
  const readoutEl = useRef<HTMLDivElement | null>(null);
  const moveShown = !pictureUnavailable && suggestionToMove !== null;
  useLayoutEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && screenRef.current?.contains(active)) return;
    if (!active || active === document.body) readoutEl.current?.focus();
  }, [moveShown]);

  /* ---------------- questions: discard, and starting again ---------------- */

  const [asking, setAsking] = useState<null | { kind: 'discard' } | { kind: 'start-again' }>(null);
  const unsaved = draft.dirty && (draft.pairs.length > 0 || draft.edges.length > 0);
  /** Cancel, Close and Escape: ask first when there is anything unsaved (R10). */
  const requestClose = () => {
    if (unsaved) setAsking({ kind: 'discard' });
    else onClose('cancelled');
  };
  /** Start again (R7): clears every spot, after asking when anything is placed. */
  const requestStartAgain = () => {
    if (draft.pairs.length > 0 || draft.edges.length > 0) setAsking({ kind: 'start-again' });
    else dispatch({ type: 'start-again' });
  };
  /*
   * Add another spot and Recalibrate each go once pressed. Focus moves to the picture, where the
   * next spot is placed, rather than falling to the page outside the dialog.
   */
  const addSpot = () => {
    dispatch({ type: 'add-spot' });
    zoom.paneRef.current?.focus();
  };
  const recalibrate = () => {
    dispatch({ type: 'start-again' });
    zoom.paneRef.current?.focus();
  };
  /** "Can't see it" (R2): this corner is set aside for the session and the next one is offered. */
  const skipTarget = () => {
    if (target) dispatch({ type: 'skip', key: target.key });
    else if (guided.status === 'exhausted') say(NO_MORE_CORNERS);
  };
  /**
   * Another corner (findable-spots R4.4): the next suggestion, without setting this corner aside.
   * Once every open corner has been passed, the choice starts over from them all.
   */
  const anotherCorner = () => {
    if (!target) {
      // Suggestions exhausted: step through the open corners outside every tile, in turn, so the
      // keyboard reaches every corner a plan click can (R4.4).
      if (guided.status !== 'exhausted') return;
      const taken = takenKeys(draft);
      const open = corners.filter(c => !taken.has(c.key));
      if (open.length === 0) {
        say(NO_MORE_CORNERS);
        return;
      }
      const next = open.find(c => !passed.includes(c.key));
      setSteer({
        chosen: (next ?? open[0]).key,
        passed: next ? passed : NO_KEYS,
        pairs: draft.pairs,
        skipped: draft.skipped,
      });
      return;
    }
    setSteer({
      chosen: null,
      // A corner already passed means the choice has come round: start a new round from it, so
      // every press moves on (R4.4).
      passed: passed.includes(target.key) ? [target.key] : [...passed, target.key],
      pairs: draft.pairs,
      skipped: draft.skipped,
    });
  };
  /** A click on the plan (findable-spots R4.1, R4.2): the nearest open corner becomes the target. */
  const pickOnPlan = (point: { x: number; y: number }) => {
    // Exhausted still has open corners outside every tile: a pick may name one (R4.1).
    if (guided.status !== 'asking' && guided.status !== 'exhausted') return;
    const corner = cornerNear(point, corners, takenKeys(draft));
    if (!corner) {
      say(NO_CORNER_THERE);
      return;
    }
    // The same corner picked again changes no state, so its announcement is said here.
    if (corner.key === chosen) say(`You chose the corner of ${corner.name}`);
    else setSteer({ chosen: corner.key, passed, pairs: draft.pairs, skipped: draft.skipped });
  };

  /* ---------------- placing on the picture ---------------- */

  /** A picture point placed: a traced edge's next point, or the spot for the target (R1). */
  const placeAt = (point: Point) => {
    if (draft.tracingEdgeId) {
      dispatch({ type: 'trace-point', point });
      return;
    }
    if (!target) return;
    justPlaced.current = placedCount + 1;
    dispatch({
      type: 'place',
      picture: point,
      plan: [target.plan.x, target.plan.y],
      target: { itemRefid: target.itemRefid, corner: target.corner },
    });
  };

  const press = useRef<{
    x: number;
    y: number;
    last: { x: number; y: number };
    moved: boolean;
    marker: string | null;
  } | null>(null);

  /*
   * A marker drag refits on release, not on every move (camera-model R18): while dragging, the marker
   * follows the pointer from local state, redrawn at most once per animation frame, and one `move`
   * is dispatched when it is dropped. The overlay and readout update then.
   */
  const dragFrame = useRef<number | null>(null);
  const dragPoint = useRef<{ id: string; point: Point } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ id: string; point: Point } | null>(null);
  const showDrag = useCallback(() => {
    dragFrame.current = null;
    setDragPreview(dragPoint.current);
  }, []);
  useEffect(
    () => () => {
      if (dragFrame.current !== null) cancelAnimationFrame(dragFrame.current);
    },
    [],
  );

  const insideFrame = (p: { x: number; y: number }) =>
    natural !== null && p.x >= 0 && p.y >= 0 && p.x < natural.width && p.y < natural.height;

  const onPaneMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    setPictureCross(null);
    const pressed = (e.target as HTMLElement).closest<HTMLElement>('[data-pair-id]');
    press.current = {
      x: e.clientX,
      y: e.clientY,
      last: { x: e.clientX, y: e.clientY },
      moved: false,
      marker: pressed?.dataset.pairId ?? null,
    };
    const onMove = (ev: MouseEvent) => {
      const p = press.current;
      if (!p) return;
      if (!p.moved && Math.hypot(ev.clientX - p.x, ev.clientY - p.y) > CLICK_TOLERANCE)
        p.moved = true;
      if (!p.moved) return;
      if (p.marker) {
        const at = zoom.clientToNatural(ev.clientX, ev.clientY);
        if (insideFrame(at)) {
          dragPoint.current = { id: p.marker, point: [at.x, at.y] };
          if (dragFrame.current === null) dragFrame.current = requestAnimationFrame(showDrag);
        }
      } else {
        zoom.panBy(ev.clientX - p.last.x, ev.clientY - p.last.y);
      }
      p.last = { x: ev.clientX, y: ev.clientY };
    };
    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (dragFrame.current !== null) cancelAnimationFrame(dragFrame.current);
      dragFrame.current = null;
      const dragged = dragPoint.current;
      dragPoint.current = null;
      setDragPreview(null);
      if (dragged) dispatch({ type: 'move', id: dragged.id, point: dragged.point });
      const p = press.current;
      press.current = null;
      if (!p || p.moved) return;
      if (p.marker) {
        dispatch({ type: 'select', id: p.marker });
        return;
      }
      if (!pictureReady) return;
      const at = zoom.clientToNatural(ev.clientX, ev.clientY);
      if (insideFrame(at)) placeAt([at.x, at.y]);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  /*
   * Whether the plan is drawn (`onboarding-3d-plan-calibration-plan-3d`, R2.2), as its canvas tells it:
   * until it is, the plan pane shows its loading block and its name says "loading".
   */
  const [planDrawn, setPlanDrawn] = useState(false);
  /* Renderer-switch R2.5: the 3D plan could not be drawn; its canvas shows its own message. */
  const [planFailed, setPlanFailed] = useState(false);

  /* ---------------- keyboard placement (Block A 1) ---------------- */

  const screenRef = useRef<HTMLElement | null>(null);
  /** Leave the picture: focus goes to the dialog itself, and a second Escape closes it. */
  const leavePane = () => screenRef.current?.focus();
  const selected = draft.pairs.find(p => p.id === draft.selectedPairId) ?? null;

  /** The picture's crosshair, in natural px: shown once an arrow key moves it, until a pointer press. */
  const [pictureCross, setPictureCross] = useState<{ x: number; y: number } | null>(null);
  const onPictureKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || !natural) return;
    const step = ((e.shiftKey ? 10 : 1) * PICTURE_STEP_PX) / zoom.view.scale;
    const from = pictureCross ??
      (selected ? { x: selected.picture[0], y: selected.picture[1] } : null) ?? {
        x: natural.width / 2,
        y: natural.height / 2,
      };
    const moveTo = (dx: number, dy: number) =>
      setPictureCross({
        x: Math.min(natural.width - 1, Math.max(0, from.x + dx)),
        y: Math.min(natural.height - 1, Math.max(0, from.y + dy)),
      });
    switch (e.key) {
      case 'ArrowLeft':
        moveTo(-step, 0);
        break;
      case 'ArrowRight':
        moveTo(step, 0);
        break;
      case 'ArrowUp':
        moveTo(0, -step);
        break;
      case 'ArrowDown':
        moveTo(0, step);
        break;
      case ' ':
        // While tracing an edge, Space adds the crosshair's point to it: Enter finishes the edge.
        if (!draft.tracingEdgeId || !pictureCross || !pictureReady) return;
        dispatch({ type: 'trace-point', point: [pictureCross.x, pictureCross.y] });
        break;
      case 'Enter':
        // Tracing: Enter finishes the edge (the banner's "Press Enter when done"), never adds to it.
        if (draft.tracingEdgeId) {
          dispatch({ type: 'finish-trace' });
        } else if (pictureCross && pictureReady && target) {
          placeAt([pictureCross.x, pictureCross.y]);
        } else {
          return;
        }
        break;
      case 'Delete':
      case 'Backspace':
        if (!selected) return;
        dispatch({ type: 'remove-pair', id: selected.id });
        break;
      case 'Escape':
        setPictureCross(null);
        leavePane();
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  /*
   * A message floats over the top strip of the panes, so whatever it covers is hidden. It turns
   * see-through while the pointer or the picture's keyboard crosshair is under it.
   */
  const messageRef = useRef<HTMLDivElement>(null);
  const crosshairRef = useRef<HTMLSpanElement>(null);
  const [pointerUnderMessage, setPointerUnderMessage] = useState(false);
  /** The pointer's last place over the panes, so a message that changes size is re-checked. */
  const pointerAt = useRef<{ x: number; y: number } | null>(null);
  const [crosshairUnderMessage, setCrosshairUnderMessage] = useState(false);
  const underMessage = (x: number, y: number) => {
    const box = messageRef.current?.getBoundingClientRect();
    return (
      !!box && box.width > 0 && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom
    );
  };

  /** Escape anywhere else asks to close; Tab stays inside the dialog. */
  const onScreenKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (asking) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      requestClose();
      return;
    }
    if (e.key !== 'Tab' || !screenRef.current) return;
    const focusables = Array.from(
      screenRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex="0"], input:not([disabled])',
      ),
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === screenRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && at === last) {
      e.preventDefault();
      first.focus();
    }
  };
  // Focus moves into the dialog when it opens.
  useEffect(() => {
    screenRef.current?.focus();
  }, []);

  /* ---------------- markers ---------------- */

  const numberOf = (id: string) => pairNumber(draft.pairs, id);
  const markerState = (p: DraftPair): PointMarkerState => {
    if (badIds.has(p.id)) return 'bad';
    if (p.id === draft.selectedPairId) return 'selected';
    return p.source === 'observer' ? 'proposed' : 'placed';
  };
  const planMarkers: CanvasPointMarker[] = draft.pairs.map(p => ({
    id: p.id,
    x: p.plan[0],
    y: p.plan[1],
    label: String(numberOf(p.id)),
    state: markerState(p),
  }));
  if (target) {
    planMarkers.push({
      id: 'target',
      x: target.plan.x,
      y: target.plan.y,
      label: '+',
      state: 'crosshair',
    });
  }

  /* ---------------- save and close ---------------- */

  const save = () => {
    if (!saveable || !natural || !camera.rtspEndpointBid) return;
    const calibration = buildCalibration({
      draft,
      fits,
      ...setup,
      rtspEndpointBid: camera.rtspEndpointBid,
      frame: natural,
      fittedAt: new Date().toISOString(),
    });
    onWrite(camera.refid, { calibration }, true);
    dispatch({ type: 'saved' });
    onClose('saved');
  };

  /* ---------------- the view ---------------- */

  const tiled = setup.layout === 'grid-2x2';
  const fisheye = setup.lens === 'fisheye';
  const guidedRun = draft.mode === 'guided';
  const taken = takenAt(feed.capturedAt);
  const sub = [
    floorName,
    natural ? `picture ${natural.width} × ${natural.height}` : null,
    tiled && natural
      ? `four tiles of ${Math.floor(natural.width / 2)} × ${Math.floor(natural.height / 2)}`
      : null,
    taken ? `taken ${taken}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const nearLine = fits.some(f => f.verdict === 'near-line');
  const tracing = draft.tracingEdgeId
    ? draft.edges.findIndex(e => e.id === draft.tracingEdgeId)
    : -1;

  /** The guided step's one line (R1–R4, the Inputs table and Failure States). */
  const instruction = (() => {
    switch (guided.status) {
      case 'saved':
        return targetsMoved
          ? 'A machine this calibration was fitted against has moved, so it is out of date. Recalibrate to place new spots.'
          : 'This is the saved calibration. Recalibrate to place new spots.';
      case 'reading':
        return 'Reading machine sizes…';
      case 'nothing':
        return 'Nothing on the plan to match: no machine on this floor has a readable size.';
      case 'exhausted':
        return NO_MORE_CORNERS;
      case 'done':
        return `${countWord(placedCount)} spots placed: you can save, or add another spot.`;
      default:
        return target
          ? [`Find the highlighted corner of ${target.name} on the picture and click it.`, lookText]
              .filter(Boolean)
              .join(' ')
          : '';
    }
  })();
  const progressText =
    guided.status === 'asking'
      ? `Spot ${spotOf} of ${guided.wanted}`
      : `${placedCount} of ${guided.wanted} spots`;

  // Which message is up, so the see-through check re-measures against the one now showing.
  const messageKey = sizeNote ?? (nearLine ? 'near-line' : tracing >= 0 ? `trace-${tracing}` : '');
  useLayoutEffect(() => {
    const at = pointerAt.current;
    setPointerUnderMessage(!!at && underMessage(at.x, at.y));
    const mark = crosshairRef.current?.getBoundingClientRect();
    setCrosshairUnderMessage(
      !!mark && underMessage(mark.left + mark.width / 2, mark.top + mark.height / 2),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pictureCross, pictureReady, zoom.view, messageKey]);
  const messagesSeeThrough = pointerUnderMessage || crosshairUnderMessage;

  return (
    <Screen
      ref={screenRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={onScreenKeyDown}
      data-planner-shortcuts="off"
      data-testid="camera-calibration"
    >
      <Header>
        <Heading>
          <h3 id={titleId}>Calibrate camera — {cameraName}</h3>
          <span>{sub}</span>
        </Heading>
        {/* R5: chosen for the admin, shown and never changeable. */}
        <Choice role="group" aria-label="Lens and layout, chosen for this camera">
          <Tag surface="on-glass" size="dense">
            {fisheye ? 'Fisheye lens' : 'Standard lens'}
          </Tag>
          <Tag surface="on-glass" size="dense">
            {tiled ? 'Four tiles' : 'Single view'}
          </Tag>
        </Choice>
      </Header>

      <Panes
        data-testid="calibration-panes"
        onMouseMove={e => {
          pointerAt.current = { x: e.clientX, y: e.clientY };
          const under = underMessage(e.clientX, e.clientY);
          if (under !== pointerUnderMessage) setPointerUnderMessage(under);
        }}
        onMouseLeave={() => {
          pointerAt.current = null;
          setPointerUnderMessage(false);
        }}
      >
        <MessageLayer
          ref={messageRef}
          data-testid="calibration-messages"
          $seeThrough={messagesSeeThrough}
          data-see-through={messagesSeeThrough || undefined}
        >
          {sizeNote ? (
            <Banner surface="on-glass" tone="warning" title="The camera picture changed size.">
              {sizeNote}
            </Banner>
          ) : nearLine ? (
            <Banner surface="on-glass" tone="warning" title="Points are too close to a line.">
              The spots so far lie close to one line. Keep placing the corners asked for: the next
              ones spread them out.
            </Banner>
          ) : tracing >= 0 ? (
            <Banner surface="on-glass" tone="info" title={`Tracing edge ${edgeLetter(tracing)}.`}>
              Click along something straight in the room — a floor-tile seam or the base of a wall.
              From the keyboard, move the crosshair with the arrow keys and press Space for each
              point. Press Enter when done.
            </Banner>
          ) : null}
        </MessageLayer>
        <PictureColumn>
          <PaneCaption>
            <span>Camera picture</span>
            <span>
              {zoom.zoomPercent} %{tiled ? ' · tile lines show where each view ends' : ''}
            </span>
          </PaneCaption>
          <PicturePane
            ref={zoom.paneRef}
            tabIndex={0}
            // It takes its own arrow keys, Enter, Space and Delete (Block A 1), so screen readers pass them on.
            role="application"
            aria-label="Camera picture: place the highlighted corner"
            onMouseDown={onPaneMouseDown}
            onKeyDown={onPictureKeyDown}
            onBlur={e => {
              if (e.target === e.currentTarget) setPictureCross(null);
            }}
            data-testid="calibration-picture"
          >
            {imageUrl && !pictureFailed ? (
              <PictureLayer
                style={{
                  transform: `translate(${zoom.view.x}px, ${zoom.view.y}px) scale(${zoom.view.scale})`,
                  width: shownSize?.width,
                  height: shownSize?.height,
                }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  draggable={false}
                  onLoad={onPictureLoad}
                  onError={onPictureError}
                  data-testid="calibration-picture-image"
                />
                {natural ? (
                  <svg
                    aria-hidden="true"
                    viewBox={`0 0 ${natural.width} ${natural.height}`}
                    width={natural.width}
                    height={natural.height}
                    data-testid="calibration-overlay"
                  >
                    <path className="rooms" d={overlay.rooms} />
                    <path className="machines" d={overlay.machines} />
                    <path className="tiles" d={overlay.tiles} />
                    <path className="residuals" d={overlay.residuals} />
                    {fisheye ? <path className="edge" d={overlay.edges} /> : null}
                  </svg>
                ) : null}
              </PictureLayer>
            ) : null}

            {!natural && !pictureUnavailable ? (
              <PaneWait>
                <Skeleton shape="block" />
              </PaneWait>
            ) : null}

            {pictureUnavailable ? (
              <PictureFailed>
                <Icon name="image" />
                <strong>The camera picture could not be loaded.</strong>
                {draft.pairs.length ? (
                  <span>Your {plural(draft.pairs.length, 'spot')} are kept.</span>
                ) : null}
                {onRetryPicture ? (
                  <Button
                    variant="frosted"
                    size="xs"
                    onClick={() => {
                      setPictureFailed(false);
                      onRetryPicture(feed.id);
                    }}
                  >
                    Retry
                  </Button>
                ) : null}
              </PictureFailed>
            ) : null}

            {pictureReady && fisheye
              ? draft.edges.flatMap((e, k) =>
                  e.points.map((p, i) => {
                    const at = naturalToPane(zoom.view, { x: p[0], y: p[1] });
                    return (
                      <EdgePoint
                        key={`${e.id}-${i}`}
                        aria-hidden="true"
                        title={`Edge ${edgeLetter(k)}`}
                        style={{ left: at.x, top: at.y }}
                      />
                    );
                  }),
                )
              : null}

            {pictureReady && tiled
              ? fits.map(f => {
                  const at = naturalToPane(zoom.view, { x: f.rect[0], y: f.rect[1] });
                  const summary = tileSummary(f);
                  return (
                    <TileLabel
                      key={f.index}
                      $tone={summary.tone}
                      style={{ left: at.x + 8, top: at.y + 8 }}
                    >
                      {tileLetter(f.index)} · {summary.text}
                    </TileLabel>
                  );
                })
              : null}

            {pictureReady
              ? draft.pairs.map(p => {
                  // While dragged, the marker follows the pointer; the spot moves on release (R18).
                  const shown = dragPreview?.id === p.id ? dragPreview.point : p.picture;
                  const at = naturalToPane(zoom.view, { x: shown[0], y: shown[1] });
                  const k = numberOf(p.id);
                  return (
                    <PointMarker
                      key={p.id}
                      data-pair-id={p.id}
                      state={markerState(p)}
                      label={String(k)}
                      x={at.x}
                      y={at.y}
                      aria-label={`Spot ${k}, picture`}
                      aria-pressed={p.id === draft.selectedPairId}
                      // Enter or Space (a click with no pointer, detail 0) selects the spot; a mouse
                      // press is the pane's (it tells a click from a drag), so it is not counted twice.
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (e.detail === 0) dispatch({ type: 'select', id: p.id });
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        if (e.key === 'Delete' || e.key === 'Backspace') {
                          dispatch({ type: 'remove-pair', id: p.id });
                        } else if (e.key !== 'Escape') {
                          return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        zoom.paneRef.current?.focus();
                      }}
                    />
                  );
                })
              : null}

            {pictureReady && pictureCross
              ? (() => {
                  const at = naturalToPane(zoom.view, pictureCross);
                  return (
                    <Crosshair
                      ref={crosshairRef}
                      aria-hidden="true"
                      data-testid="calibration-picture-crosshair"
                      style={{ left: at.x, top: at.y }}
                    />
                  );
                })()
              : null}
          </PicturePane>
          <Legend>
            <span>
              <LineSample look={room} />
              Rooms and areas
            </span>
            <span>
              <LineSample look={machine} />
              Machines
            </span>
            <span>
              <LineSample look={spotError} />
              Spot error
            </span>
            {fisheye ? (
              <span>
                <Diamond />
                Traced edge
              </span>
            ) : null}
            <LegendHint>Scroll to zoom · drag to pan</LegendHint>
          </Legend>
          {/* Present from mount, so its text is announced once when footprints settle (R5). */}
          <StandInNote role="status" data-testid="calibration-stand-ins">
            {standIns ?? ''}
          </StandInNote>
        </PictureColumn>

        <PlanColumn>
          <PaneCaption>
            <span>Floor plan — {floorName}</span>
          </PaneCaption>
          <PlanPane data-testid="calibration-plan">
            {/* ReadOnlyCanvas styles itself from the planner theme. The Observers stage wraps it the
                same way; this screen renders outside TwinPlanner, so it brings its own. It zooms,
                pans and takes a click on a corner (findable-spots R1.3, R4). */}
            <ThemeProvider theme={plannerTheme}>
              <ReadOnlyCanvas
                layout={layout}
                floorIndex={floorIndex}
                driverName="calibration"
                keepViewOnResize
                viewRotation={planRotation}
                fitBounds={planFitBounds}
                markers={planOutlines}
                pointMarkers={planMarkers}
                onPlanPointClick={pickOnPlan}
                ariaRole="region"
                ariaLabel={`${PLAN_PANE_NAME}${planDrawn || planFailed ? '' : ', loading'}`}
                onDrawnChange={setPlanDrawn}
                onDrawFailedChange={setPlanFailed}
              />
            </ThemeProvider>
            {/* R2.2: the picture pane's loading block, over the plan until it is drawn, or until the
                plan says it could not be drawn (renderer-switch R2.5). */}
            {planDrawn || planFailed ? null : (
              <PaneWait aria-hidden="true" data-testid="calibration-plan-loading">
                <Skeleton shape="block" />
              </PaneWait>
            )}
          </PlanPane>
        </PlanColumn>
      </Panes>

      <GuidedBar data-testid="calibration-guided">
        <GuidedProgress>
          <span aria-hidden="true">{progressText}</span>
          <Progress
            label="Calibration spots placed"
            value={Math.min(placedCount, guided.wanted)}
            max={guided.wanted}
            valueText={progressText}
          />
        </GuidedProgress>
        <GuidedText>
          <Note tone={guided.status === 'asking' ? 'peri' : 'neutral'}>{instruction}</Note>
        </GuidedText>
        {guidedRun ? (
          <Button
            variant="secondary"
            size="sm"
            aria-disabled={!target && guided.status !== 'exhausted'}
            onClick={anotherCorner}
            data-testid="calibration-another"
          >
            Another corner
          </Button>
        ) : null}
        {guidedRun ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label="Can't see this corner"
            aria-disabled={!target}
            onClick={skipTarget}
            data-testid="calibration-skip"
          >
            Can’t see it
          </Button>
        ) : null}
        {guidedRun && guided.status === 'done' ? (
          <Button variant="text" size="sm" onClick={addSpot}>
            Add another spot
          </Button>
        ) : null}
      </GuidedBar>

      <Footer>
        <Chips aria-label="Spots">
          {draft.pairs.map(p => {
            const k = numberOf(p.id);
            const tile = tiled && natural ? tileOf(fits, p.picture) : '';
            const pairError = errorOf.get(p.id);
            const err = pairError
              ? [
                  pairError.px === null ? null : `${Math.round(pairError.px)} px`,
                  pairError.m === null ? null : `${pairError.m.toFixed(2)} m`,
                  badIds.has(p.id) ? 'check this one' : null,
                  disagreeingIds.has(p.id) ? DISAGREE_NOTE : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : '—';
            const errorWords =
              pairError?.px == null ? 'no error yet' : `error ${Math.round(pairError.px)} pixels`;
            return (
              <li
                key={p.id}
                // Block A 1: Delete on a chip removes its spot.
                onKeyDown={e => {
                  if (e.key !== 'Delete') return;
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch({ type: 'remove-pair', id: p.id });
                }}
              >
                <Tag
                  surface="on-glass"
                  size="dense"
                  tone={
                    badIds.has(p.id) ? 'pink' : p.source === 'observer' ? 'lavender' : 'neutral'
                  }
                  removable
                  removeLabel={`Spot ${k}, ${errorWords}, remove`}
                  onRemove={() => dispatch({ type: 'remove-pair', id: p.id })}
                >
                  <ChipNumber>
                    {tile}
                    {k}
                  </ChipNumber>
                  <ChipError>{err}</ChipError>
                </Tag>
              </li>
            );
          })}
          {fisheye
            ? draft.edges.map((e, k) => (
                <li key={e.id}>
                  <Tag
                    surface="on-glass"
                    size="dense"
                    tone={e.points.length >= MIN_EDGE_POINTS ? 'lavender' : 'butter'}
                    removable
                    removeLabel={`Remove edge ${edgeLetter(k)}`}
                    onRemove={() => dispatch({ type: 'remove-edge', id: e.id })}
                  >
                    <ChipNumber>Edge {edgeLetter(k)}</ChipNumber>
                    <ChipError>
                      {e.points.length >= MIN_EDGE_POINTS
                        ? 'straight'
                        : `${e.points.length} of ${MIN_EDGE_POINTS} points`}
                    </ChipError>
                  </Tag>
                </li>
              ))
            : null}
        </Chips>
        {readout ? (
          <Readout
            ref={readoutEl}
            $tone={readout.tone}
            tabIndex={-1}
            data-testid="calibration-readout"
          >
            <ReadoutDot $tone={readout.tone} aria-hidden="true" />
            {pictureReady ? readout.text : 'Waiting for the picture'}
          </Readout>
        ) : (
          <Readout ref={readoutEl} $tone="idle" tabIndex={-1} data-testid="calibration-readout">
            <ReadoutDot $tone="idle" aria-hidden="true" />
            Waiting for the picture
          </Readout>
        )}
        {moveShown && suggestionToMove ? (
          <MarkerNote data-testid="calibration-marker-note">
            <Note tone="peri" icon={<Icon name="circle-info" size="xs" />}>
              The fit puts this camera {suggestionToMove.distanceM.toFixed(1)} m from its marker on
              the plan.
            </Note>
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Move camera marker to fitted position, ${suggestionToMove.distanceM.toFixed(
                1,
              )} m away`}
              onClick={moveMarker}
              data-testid="calibration-move-marker"
            >
              Move marker
            </Button>
          </MarkerNote>
        ) : null}
        <Actions>
          {guidedRun ? (
            <Button variant="text" size="sm" onClick={requestStartAgain}>
              Start again
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={recalibrate}>
              Recalibrate
            </Button>
          )}
          {/* R8: traced straight edges stay, as an optional step, with a fisheye lens only. */}
          {fisheye ? (
            <Tag
              surface="on-glass"
              toggle
              active={tracing >= 0}
              tone="lavender"
              icon={<Icon name="pen-ruler" size="xs" />}
              onToggle={() => dispatch({ type: 'toggle-trace' })}
            >
              Trace a straight edge
            </Tag>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            aria-disabled={!saveable}
            onClick={save}
            data-testid="calibration-save"
          >
            Save calibration
          </Button>
          <Button variant="secondary" size="sm" onClick={requestClose}>
            Cancel
          </Button>
        </Actions>
      </Footer>

      <CloseCorner>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Close calibration"
          iconLeft={<Icon name="xmark" />}
          onClick={requestClose}
        />
      </CloseCorner>

      <SrOnly role="status" data-testid="calibration-announcement">
        {polite}
      </SrOnly>
      <SrOnly role="alert" data-testid="calibration-alert">
        {pictureUnavailable ? 'The camera picture could not be loaded' : (sizeNote ?? '')}
      </SrOnly>

      <Modal
        open={asking?.kind === 'discard'}
        onClose={() => setAsking(null)}
        title={
          draft.pairs.length > 0
            ? `Discard ${plural(draft.pairs.length, 'unsaved spot')}?`
            : 'Discard your traced edges?'
        }
        tone="coral"
        width="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setAsking(null)}>
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setAsking(null);
                onClose('cancelled');
              }}
            >
              Discard
            </Button>
          </>
        }
      >
        The camera keeps its current calibration. The spots you placed here are lost.
      </Modal>
      <Modal
        open={asking?.kind === 'start-again'}
        onClose={() => setAsking(null)}
        title={`Start again with no spots?`}
        tone="coral"
        width="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setAsking(null)}>
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setAsking(null);
                dispatch({ type: 'start-again' });
              }}
            >
              Start again
            </Button>
          </>
        }
      >
        {`Your ${plural(draft.pairs.length, 'spot')} are cleared and the first corner is asked for again. Corners you couldn’t see stay skipped.`}
      </Modal>
    </Screen>
  );
};

/** A spot chip's tile letter on a four-tile picture. */
const tileOf = (fits: ReadonlyArray<ViewFit>, picture: Point): string => {
  const f = fits.find(
    v =>
      picture[0] >= v.rect[0] &&
      picture[0] < v.rect[0] + v.rect[2] &&
      picture[1] >= v.rect[1] &&
      picture[1] < v.rect[1] + v.rect[3],
  );
  return f ? tileLetter(f.index) : '';
};

export default CameraCalibration;

/* ---------------- styles: tokens only ---------------- */

/* Visually hidden, still announced: the clip-rect recipe, never display:none. */
const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* The picture's keyboard crosshair (Block A 1): a ring with a cross, at one size on screen. */
const Crosshair = styled.span`
  position: absolute;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
  border: 2px solid ${CALIBRATION_LOOK.overlay.crosshair};
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${rgba(ink[1], 0.6)};
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    background: ${CALIBRATION_LOOK.overlay.crosshair};
    transform: translate(-50%, -50%);
  }

  &::before {
    width: 2px;
    height: 32px;
  }

  &::after {
    width: 32px;
    height: 2px;
  }
`;

const Screen = styled.section`
  &:focus {
    outline: none;
  }

  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  padding: ${space[4]};
  box-sizing: border-box;
  background: ${surface[2]};
  color: ${ink[1]};
  overflow: hidden;
`;

const Header = styled.div`
  position: relative;
  padding-right: ${space[6]};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${space[3]};
`;

const Heading = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  h3 {
    margin: 0;
    font-size: ${typeRole.subtitle2.fontSize};
    line-height: ${typeRole.subtitle2.lineHeight};
    font-weight: ${weight.semibold};
  }

  span {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.body};
  }
`;

const Choice = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[1]};
`;

const Panes = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  gap: ${space[3]};
`;

/*
 * The screen's messages float over the top of the panes rather than sitting above them, so a
 * message arriving mid-click (the near-line refusal) never moves the picture or the plan. Clicks
 * pass through; none of these banners carries a control.
 */
const MessageLayer = styled.div<{ $seeThrough: boolean }>`
  position: absolute;
  top: ${space[3]};
  left: 50%;
  z-index: 2;
  width: min(640px, calc(100% - ${space[6]}));
  transform: translateX(-50%);
  pointer-events: none;
  opacity: ${({ $seeThrough }) => ($seeThrough ? 0.4 : 1)};
  transition: opacity ${motion.durFast} ${motion.easeUi};

  &:empty {
    display: none;
  }
`;

const PictureColumn = styled.div`
  flex: 3 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
`;

const PlanColumn = styled.div`
  flex: 2 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
`;

const PaneCaption = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.body};
`;

const PicturePane = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: ${radius.xl};
  overflow: hidden;
  background: ${ink[1]};
  box-shadow: ${shadow.glassSm};
  cursor: crosshair;
  user-select: none;

  &:focus-visible {
    outline: none;
    box-shadow: ${focus.ring};
  }
`;

const PictureLayer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none;
  }

  svg {
    position: absolute;
    inset: 0;
    overflow: visible;
    pointer-events: none;
  }

  path {
    vector-effect: non-scaling-stroke;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  .rooms {
    fill: none;
    stroke: ${room.stroke};
    stroke-width: ${room.width};
    stroke-dasharray: ${room.dash};
  }

  .machines {
    fill: none;
    stroke: ${machine.stroke};
    stroke-width: ${machine.width};
    stroke-dasharray: ${machine.dash};
    stroke-linecap: ${machine.lineCap};
  }

  .tiles {
    fill: none;
    stroke: ${rgba(surface[0], 0.85)};
    stroke-width: 2;
    stroke-dasharray: 9 6;
  }

  .residuals {
    fill: none;
    stroke: ${spotError.stroke};
    stroke-width: ${spotError.width};
  }

  .edge {
    fill: none;
    stroke: ${surface[0]};
    stroke-width: 2;
    stroke-dasharray: 7 5;
  }
`;

/** A pane's loading block, laid over the whole pane: the picture's, and the plan's until it draws. */
const PaneWait = styled.div`
  position: absolute;
  inset: 0;
  display: flex;

  > * {
    flex: 1;
  }
`;

const PictureFailed = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  color: ${surface[1]};
  font-size: ${typeRole.body1.fontSize};
  line-height: ${typeRole.body1.lineHeight};

  span {
    font-size: ${typeRole.caption1.fontSize};
    color: ${line.control};
  }
`;

const EdgePoint = styled.span`
  position: absolute;
  width: 9px;
  height: 9px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: ${surface[0]};
  border: 2px solid ${inkOn.lav};
  pointer-events: none;
`;

const TileLabel = styled.span<{ $tone: ReadoutTone }>`
  position: absolute;
  padding: 2px ${space[2]};
  border-radius: ${radius.pill};
  border: 1px solid ${surface[0]};
  background: ${rgba(surface[0], 0.92)};
  color: ${({ $tone }) => CALIBRATION_LOOK.readout[$tone].ink};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  font-weight: ${weight.semibold};
  pointer-events: none;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[3]};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.body};

  > span {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
  }
`;

const LegendHint = styled.span`
  margin-left: auto;
`;

/**
 * The stand-in count beneath the legend. Always rendered, even empty, so the live region is in the
 * accessibility tree before its text arrives (a region shown already holding text is not reliably
 * announced); when empty it takes back the column's gap rather than hiding.
 */
const StandInNote = styled.div`
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};

  &:empty {
    margin-top: calc(-1 * ${space[1]});
  }
`;

/**
 * A legend sample: a short run of an overlay's line, its colour, width and dash, on the picture
 * pane's dark ground, since the faint frost room line does not show on the light legend.
 */
const LineSample: React.FC<{
  look: { stroke: string; width: number; dash?: string; lineCap?: 'butt' | 'round' };
}> = ({ look }) => (
  <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
    <rect width="22" height="10" rx="2" fill={ink[1]} />
    <line
      x1="3"
      y1="5"
      x2="19"
      y2="5"
      stroke={look.stroke}
      strokeWidth={Math.min(look.width, 2.5)}
      strokeDasharray={look.dash}
      strokeLinecap={look.lineCap ?? 'butt'}
    />
  </svg>
);

const Diamond = styled.span`
  width: 8px;
  height: 8px;
  transform: rotate(45deg);
  background: ${surface[0]};
  border: 2px solid ${inkOn.lav};
`;

const PlanPane = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: ${radius.xl};
  overflow: hidden;
  background: ${surface[1]};
  border: 1px solid ${line[2]};
  display: flex;

  > * {
    flex: 1;
    min-width: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
`;

const Chips = styled.ul`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ChipNumber = styled.span`
  font-weight: ${weight.semibold};
  margin-right: ${space[1]};
`;

const ChipError = styled.span`
  font-family: ${fontMono};
`;

const Readout = styled.p<{ $tone: ReadoutTone }>`
  &:focus-visible {
    outline: none;
    border-radius: ${radius.md};
    box-shadow: ${focus.ring};
  }

  flex: none;
  max-width: 340px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${space[2]};
  font-size: ${typeRole.body1.fontSize};
  line-height: ${typeRole.body1.lineHeight};
  font-weight: ${weight.semibold};
  color: ${({ $tone }) => CALIBRATION_LOOK.readout[$tone].ink};
`;

const ReadoutDot = styled.span<{ $tone: ReadoutTone }>`
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $tone }) => CALIBRATION_LOOK.readout[$tone].dot};
`;

const Actions = styled.div`
  flex: none;
  display: flex;
  gap: ${space[2]};
`;

/** The marker suggestion (camera-model R10): its note, then Move marker, between readout and Save. */
const MarkerNote = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: ${space[2]};
`;

/* The guided step (guided calibration R1–R4): progress, the one-line instruction and its actions. */
const GuidedBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  flex-wrap: wrap;
`;

const GuidedProgress = styled.div`
  flex: none;
  width: 180px;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  font-weight: ${weight.semibold};
`;

const GuidedText = styled.div`
  flex: 1;
  min-width: 240px;
`;

/* Close sits last in the tab order (Block A 1) but top right on screen, where the card puts it. */
const CloseCorner = styled.div`
  position: absolute;
  top: ${space[4]};
  right: ${space[4]};
`;
